/*
 * indexx.ai — the home page.
 *
 * One route, two designs. Signed out it makes the claim; signed in it drops
 * the pitch and becomes a directory with your name on it. Everything below the
 * top block is identical in both, which is why only the hero swaps.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CountsStrip from "./CountsStrip";
import ProductGrid from "./ProductGrid";
import ZodiacBand from "./ZodiacBand";
import LandingFooter from "./LandingFooter";
import { useZodiacSession } from "../Home/useZodiacSession";
import { fetchProducts, ZodiacProduct } from "../../data/zodiacProducts";
import s from "./landing.module.css";

export default function Landing() {
  const { status, user, firstName, signOut } = useZodiacSession();
  const [products, setProducts] = useState<ZodiacProduct[]>([]);

  useEffect(() => {
    const ac = new AbortController();
    // Never rejects — falls back to the bundled sixteen.
    fetchProducts(ac.signal).then(setProducts);
    return () => ac.abort();
  }, []);


  const signedIn = status === "authenticated";
  const email = typeof user?.email === "string" ? user.email : null;

  return (
    <div className={s.root}>
      <div className={s.shell}>
        {/*
          Fixed height, and blank while the answer is unknown.

          The silent session check settles after first paint, so there is a
          moment where neither hero is the right one. Reserving the space and
          showing nothing beats picking a side: a signed-in visitor would
          otherwise read the sales pitch and watch it swap to their own name.
        */}
        <div className={s.hero}>
          {status === "unknown" ? null : signedIn ? (
            <>
              <p className={`${s.eyebrow} ${s.eyebrowAccent}`}>Signed in</p>
              <div className={s.signedInHead}>
                <h1 className={`${s.h1SignedIn} ${s.display}`}>
                  Welcome back, <span className={s.nameSlot}>{firstName || ""}</span>.
                </h1>
                <p className={s.sectionAside}>
                  You are signed in to all sixteen. Open any one of them — nothing will
                  ask again.
                </p>
              </div>
            </>
          ) : (
            <>
              <p className={s.eyebrow}>The ecosystem of AI and crypto</p>
              <h1 className={`${s.h1} ${s.display}`}>
                One Account.
                <br />
                One Key.
              </h1>
              {/*
                No count in the prose, deliberately.

                This line used to read "opens all sixteen … and twelve more".
                Both numbers are the same fact written twice, and neither is
                derived from anything — the moment a seventeenth product ships
                from the registry, the hero is quietly wrong and the strip
                directly beneath it says so. The counts strip IS the count, and
                it is computed from the list.
              */}
              <p className={s.lede}>
                Indexx&nbsp;Zodiac is that key. One sign-in opens every Indexx product —
                Exchange, Academy, Wall&nbsp;Street, Bitcoin&nbsp;Yay and the rest. No
                second password, no second account.
              </p>
              <div className={s.ctaRow}>
                <Link className={s.ctaPrimary} to="/auth/signup-email">
                  Create an account
                </Link>
                <a className={s.ctaGhost} href="#the-sixteen">
                  See the sixteen
                </a>
              </div>
            </>
          )}
        </div>

        <CountsStrip products={products} />
      </div>

      <section className={`${s.shell} ${s.section}`} id="the-sixteen">
        <div className={s.sectionHead}>
          <div className={s.sectionHeadText}>
            <p className={s.eyebrow}>The sixteen</p>
            <h2 className={`${s.h2} ${s.display}`}>Everything here knows who you are.</h2>
          </div>
          <p className={s.sectionAside}>
            Five things you can do with one Indexx account. Open any of them and you are
            already signed in.
          </p>
        </div>

        <ProductGrid products={products} />
      </section>

      <div className={s.shell}>
        <ZodiacBand />
      </div>

      {signedIn ? (
        <div className={s.shell}>
          <div className={s.signOutBar}>
            <span>{email ? `Signed in as ${email}` : " "}</span>
            {/*
              A real <button>: this performs an action, it does not navigate.
              And it goes through notifySignedOut, which revokes the refresh
              family and then ends the provider session — not just this tab's
              storage, which would leave the IdP session alive for the next
              silent check to find.
            */}
            <button type="button" className={s.signOutLink} onClick={signOut}>
              Sign out of everything
            </button>
          </div>
        </div>
      ) : null}

      <LandingFooter />
    </div>
  );
}
